import { readSheet } from "read-excel-file/browser";
import writeXlsxFile, { type SheetData } from "write-excel-file/browser";
import type { ParseResult, RouteCustomer, RouteList } from "./types";

const REQUIRED_HEADERS = ["順番", "顧客番号", "氏名"] as const;

const normalizeHeader = (value: unknown) => String(value ?? "").trim();

const buildCustomerId = (
  order: number,
  customerNumber: string,
  name: string,
) => `${order}:${customerNumber}:${name}`;

export const downloadExcelTemplate = async () => {
  const rows: SheetData = [
    [
      { value: "順番", type: String, fontWeight: "bold" },
      { value: "顧客番号", type: String, fontWeight: "bold" },
      { value: "氏名", type: String, fontWeight: "bold" },
    ],
    [
      { value: 1, type: Number },
      { value: "100001", type: String },
      { value: "山田太郎", type: String },
    ],
    [
      { value: 2, type: Number },
      { value: "200010", type: String },
      { value: "保育園温水", type: String },
    ],
    [
      { value: 3, type: Number },
      { value: "200010", type: String },
      { value: "保育園冷水", type: String },
    ],
  ];
  await writeXlsxFile(rows, {
    columns: [{ width: 10 }, { width: 16 }, { width: 24 }],
    sheet: "巡回リスト",
  }).toFile("kensin_route_template.xlsx");
};

export const parseRouteWorkbook = async (file: File): Promise<ParseResult> => {
  const errors: string[] = [];

  if (!file.name.match(/\.(xlsx|xls)$/i)) {
    return { ok: false, errors: ["Excelファイル（.xlsx または .xls）を選択してください。"] };
  }

  const matrix: unknown[][] = await readSheet(file);

  const [headerRow, ...dataRows] = matrix;
  if (!headerRow) {
    return { ok: false, errors: ["1行目に見出し行がありません。"] };
  }

  const headers = headerRow.map(normalizeHeader);
  const indexes = REQUIRED_HEADERS.reduce(
    (acc, header) => ({
      ...acc,
      [header]: headers.indexOf(header),
    }),
    {} as Record<(typeof REQUIRED_HEADERS)[number], number>,
  );

  for (const header of REQUIRED_HEADERS) {
    if (indexes[header] === -1) {
      errors.push(`必須列「${header}」がありません。`);
    }
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  const customers: RouteCustomer[] = [];
  const seenOrders = new Set<number>();

  dataRows.forEach((row: unknown[], rowIndex: number) => {
    const displayRow = rowIndex + 2;
    const orderRaw = String(row[indexes["順番"]] ?? "").trim();
    const customerNumber = String(row[indexes["顧客番号"]] ?? "").trim();
    const name = String(row[indexes["氏名"]] ?? "").trim();

    if (!orderRaw && !customerNumber && !name) return;

    const order = Number(orderRaw);
    if (!Number.isInteger(order) || order <= 0) {
      errors.push(`${displayRow}行目: 順番は1以上の整数にしてください。`);
    }

    if (!customerNumber) {
      errors.push(`${displayRow}行目: 顧客番号が空です。`);
    }

    if (!name) {
      errors.push(`${displayRow}行目: 氏名が空です。`);
    }

    if (Number.isInteger(order) && seenOrders.has(order)) {
      errors.push(`${displayRow}行目: 順番「${order}」が重複しています。`);
    }

    if (Number.isInteger(order)) {
      seenOrders.add(order);
    }

    if (errors.length === 0 || (Number.isInteger(order) && customerNumber && name)) {
      customers.push({
        id: buildCustomerId(order, customerNumber, name),
        order,
        customerNumber,
        name,
      });
    }
  });

  if (customers.length === 0) {
    errors.push("取込できる顧客行がありません。");
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  customers.sort((a, b) => a.order - b.order);

  const routeList: RouteList = {
    version: 1,
    uploadedAt: new Date().toISOString(),
    customers,
  };

  return { ok: true, routeList };
};
